import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Switch, TextField, Typography } from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import { useEffect, useState } from "react";
import API from "../api";
import { startToast, updateToastError, updateToastSuccess, updateToastWarning } from "../utils/notification-loading";

const ACCENT = "#449095";
const PROTOCOLS = ["tcp", "udp", "icmp"];
const CLASSIFICATIONS = [
  { value: "critical", label: "Critical", color: "#ce2528" },
  { value: "high", label: "High", color: "#ff8c00" },
  { value: "alert", label: "Alert", color: "#daa520" },
  { value: "movement", label: "Movement", color: "#58a8ae" },
];

const ipRegex = /^(ANY|((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(\/(3[0-2]|[12]?[0-9]))?)$/;

const toIpUi = (value) => (value === "ANY" ? { type: "any" } : { type: "single", value, negate: false });

const initialProtocol = (row) => {
  const p = String(row?.proto ?? row?.protocol ?? "").toLowerCase();
  return PROTOCOLS.includes(p) ? p : "tcp";
};

// tipo: 0 = High, 1 = Critical, 2 = Alert, 3 = Movement
const CLASSIFICATION_BY_TIPO = ["high", "critical", "alert", "movement"];
const initialClassification = (tipo) => CLASSIFICATION_BY_TIPO[tipo] ?? "high";
// La risposta attiva è consentita solo per critical/high (come in Management → Blacklist)
const canRespond = (classification) => classification === "critical" || classification === "high";

// Crea una regola blacklist a partire da un log di sicurezza (High/Critical):
// gli IP del log sono precompilati e modificabili, il resto ha valori di default sensati.
const BlacklistRuleDialog = ({ open, setOpen, row, tipo, namePrefix = "Blacklist", initialPort = "ANY" }) => {
  const [protocol, setProtocol] = useState("tcp");
  const [srcIp, setSrcIp] = useState("ANY");
  const [dstIp, setDstIp] = useState("ANY");
  const [port, setPort] = useState("ANY");
  const [classification, setClassification] = useState("high");
  const [response, setResponse] = useState(true);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const src = (row?.src_ip || "ANY").toUpperCase();
    const dst = (row?.dest_ip || "ANY").toUpperCase();
    setProtocol(initialProtocol(row));
    setSrcIp(src);
    setDstIp(dst);
    setPort(initialPort ?? "ANY");
    setClassification(initialClassification(tipo));
    setResponse(canRespond(initialClassification(tipo)));
    setName(dst !== "ANY" ? `${namePrefix} ${src} -> ${dst}` : `${namePrefix} ${src}`);
    setSubmitting(false);
  }, [open, row, tipo, namePrefix, initialPort]);

  const responseAllowed = canRespond(classification);
  const noPort = protocol === "icmp";
  const srcErr = !ipRegex.test(srcIp);
  const dstErr = !ipRegex.test(dstIp);
  const portUp = port.toUpperCase();
  const portErr = !noPort && portUp !== "ANY" && !(/^\d+$/.test(portUp) && Number(portUp) >= 1 && Number(portUp) <= 65535);
  const allAny = srcIp === "ANY" && dstIp === "ANY" && (noPort || portUp === "ANY");

  const handleSubmit = () => {
    startToast();
    if (srcErr || dstErr || portErr) return updateToastWarning("Please fill in all required fields");
    if (allAny) return updateToastWarning("Cannot create a rule that matches all traffic");
    if (!name.trim()) return updateToastWarning("Rule name cannot be empty");
    setSubmitting(true);
    API.setBlacklist({
      protocol,
      src_ip_ui: toIpUi(srcIp),
      dst_ip_ui: toIpUi(dstIp),
      dst_port: noPort ? "ANY" : portUp,
      ruleName: name.trim(),
      response: canRespond(classification) && response,
      classification,
      action: "add",
    })
      .then(() => { updateToastSuccess("Blacklist rule added"); setOpen(false); })
      .catch((err) => {
        setSubmitting(false);
        if (err?.status === 409 || err?.response?.status === 409) return updateToastWarning("Rule already exists");
        updateToastError("Error adding rule");
      });
  };

  const pill = (selected, color) => ({
    px: 2.5, py: 0.6, borderRadius: 4, fontSize: "0.72rem", fontWeight: 700,
    border: `1px solid ${selected ? color : "#2a2a2a"}`,
    color: selected ? color : "#555",
    backgroundColor: selected ? `${color}18` : "transparent",
    "&:hover": { backgroundColor: `${color}12`, borderColor: color, color },
  });

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm"
      slotProps={{ paper: { sx: { backgroundColor: "#141414", border: "1px solid #252525", borderRadius: 2.5 } } }}
    >
      <DialogTitle sx={{ borderBottom: "1px solid #1e1e1e", py: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5}>
          <BlockIcon sx={{ color: ACCENT, fontSize: 22 }} />
          <Typography fontSize={17} fontWeight={700}>Add Blacklist Rule</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2, pb: 0, mt: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1}>
            {PROTOCOLS.map((p) => (
              <Button key={p} size="small" onClick={() => setProtocol(p)} sx={pill(protocol === p, ACCENT)}>{p.toUpperCase()}</Button>
            ))}
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <TextField
              label="Source IP" size="small" fullWidth value={srcIp}
              onChange={(e) => setSrcIp(e.target.value.toUpperCase())}
              placeholder="ANY, 1.2.3.4, 1.2.3.0/24"
              error={srcErr} helperText={srcErr ? "Invalid IP / CIDR" : " "}
            />
            <TextField
              label="Destination IP" size="small" fullWidth value={dstIp}
              onChange={(e) => setDstIp(e.target.value.toUpperCase())}
              placeholder="ANY, 1.2.3.4, 1.2.3.0/24"
              error={dstErr} helperText={dstErr ? "Invalid IP / CIDR" : " "}
            />
            {!noPort && (
              <TextField
                label="Port" size="small" sx={{ width: 120, flexShrink: 0 }} value={port}
                onChange={(e) => setPort(e.target.value)}
                error={portErr} helperText={portErr ? "Invalid port" : " "}
              />
            )}
          </Stack>

          {allAny && (
            <Box sx={{ p: 1.25, borderRadius: 1.5, backgroundColor: "rgba(192,57,43,0.08)", border: "1px solid rgba(192,57,43,0.3)" }}>
              <Typography sx={{ fontSize: "0.78rem", color: "#c0392b", fontWeight: 500 }}>
                Every field is set to "any" — this would match all traffic. Specify at least one field.
              </Typography>
            </Box>
          )}

          <TextField
            label="Rule name" size="small" fullWidth value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 80 }}
            helperText="This will appear as the detail in the security logs"
          />

          <Stack direction="row" spacing={1}>
            {CLASSIFICATIONS.map((c) => (
              <Button key={c.value} size="small" onClick={() => { setClassification(c.value); if (!canRespond(c.value)) setResponse(false); }} sx={pill(classification === c.value, c.color)}>{c.label}</Button>
            ))}
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography sx={{ fontSize: "0.88rem", fontWeight: 500, color: responseAllowed && response ? "#e0e0e0" : "#888" }}>Enable response action</Typography>
              <Typography variant="caption" sx={{ color: responseAllowed && response ? "#aaa" : "#555" }}>
                {!responseAllowed ? 'Available only for "critical" and "high" classifications' : response ? "Device will actively react when this rule triggers" : "Rule will log only — no active countermeasure"}
              </Typography>
            </Box>
            <Switch checked={responseAllowed && response} disabled={!responseAllowed} size="small" onChange={(e) => setResponse(e.target.checked)}
              sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: ACCENT }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: ACCENT } }}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ borderTop: "1px solid #1e1e1e", px: 3, py: 2 }}>
        <Button variant="outlined" onClick={() => setOpen(false)}
          sx={{ minWidth: 90, color: "#888", borderColor: "rgba(255,255,255,0.12)", "&:hover": { borderColor: "#666", backgroundColor: "rgba(255,255,255,0.04)", color: "#bbb" } }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || allAny}
          sx={{ minWidth: 110, backgroundColor: ACCENT, "&:hover": { backgroundColor: "#367a7e" }, "&.Mui-disabled": { backgroundColor: "#2a2a2a", color: "#555" } }}>
          Add Rule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlacklistRuleDialog;
